<template>
  <div ref="groupRoot" class="sp-info-card-group" :class="classModifiers">
    <slot />
  </div>
</template>

<script setup>
import { computed, useTemplateRef } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useColor } from "#ce/composables/useColor";
import { useEqualizeHeights } from "#ce/composables/useEqualizeHeights";
import { toBoolean, toCssUnit } from "#ce/utils/props";
import { props as propDefs } from "./propsGroup";

const props = defineProps(propDefs);

const { isSmScreen, breakpointModifiers } = useBreakpoints();
const { colorModifiers } = useColor(props);

const groupRoot = useTemplateRef("groupRoot");

const isConnectedMode = computed(() => toBoolean(props.connectOnSm) && isSmScreen.value);
const effectiveEqualize = computed(() => {
  if (isConnectedMode.value) {
    return false;
  }
  if (isSmScreen.value && props.equalizeHeightsSm !== undefined) {
    return toBoolean(props.equalizeHeightsSm);
  }
  return toBoolean(props.equalizeHeights);
});
const isFixedItemsPerRow = computed(() => props.itemsPerRow !== "auto" && !isConnectedMode.value && !isSmScreen.value);

const effectiveMinHeight = computed(() => toCssUnit(props.minHeight));
const effectiveMinHeightSm = computed(() => toCssUnit(props.minHeightSm));

const { equalizedHeight } = useEqualizeHeights(groupRoot, "SP-INFO-CARD", effectiveEqualize, [isSmScreen]);

const classModifiers = computed(() => [
  ...colorModifiers.value,
  ...breakpointModifiers.value,
  { "--connected": isConnectedMode.value },
  {
    [`--items-per-row-${props.itemsPerRow}`]: isFixedItemsPerRow.value,
  },
  { [`--variant-${props.variant}`]: true },
  { "--equalize": effectiveEqualize.value },
]);
</script>

<style lang="scss">
@use "./shared/info-card-theme";

:host {
  /* Layout and sizing */
  --gap: var(--sp-comp-info-card-group-gap, var(--sp-ref-scale-24));
  --gap-sm: var(--sp-comp-info-card-group-gap-sm, var(--sp-ref-scale-16));
  --card-min-width: var(--sp-comp-info-card-min-width, 15rem);

  /* Connected */
  --border-radius: var(--sp-comp-info-card-group-border-radius, var(--sp-ref-border-radius-lg));
  --background: var(--sp-comp-info-card-group-background, var(--sp-sys-color-surface));
  --box-shadow: var(--sp-comp-info-card-group-box-shadow, var(--sp-sys-elevation-3));

  --padding-block: var(--sp-comp-info-card-group-padding-block, var(--sp-ref-scale-8));
  --padding-inline: var(--sp-comp-info-card-group-padding-inline, var(--sp-ref-scale-12));
  --padding-block-item: var(--sp-comp-info-card-group-padding-block-item, var(--sp-ref-scale-12));
  --padding-inline-item: var(--sp-comp-info-card-group-padding-inline-item, var(--sp-ref-scale-8));
  --divider-color: var(--sp-comp-info-card-group-connected-divider-color, var(--sp-sys-color-outline-variant));
  --divider-inset: var(--sp-comp-info-card-group-divider-inset, 0);

  display: block;
}
</style>

<style scoped lang="scss">
@use "@ce/shared/connected-group" as *;

.sp-info-card-group {
  display: grid;
  gap: var(--gap);
  grid-template-columns: repeat(auto-fill, minmax(var(--card-min-width), 1fr));
  justify-items: stretch;
  align-items: start;

  &:not(.--connected).--equalize {
    ::slotted(sp-info-card) {
      --sp-info-card-group-min-height: v-bind(equalizedHeight);
    }
  }

  &:not(.--connected):not(.--equalize) {
    ::slotted(sp-info-card) {
      --sp-info-card-group-min-height: v-bind(effectiveMinHeight);
    }
  }

  &.--is-sm-screen {
    gap: var(--gap-sm);
    grid-template-columns: 1fr;

    &:not(.--connected).--equalize {
      ::slotted(sp-info-card) {
        --sp-info-card-group-min-height-sm: v-bind(equalizedHeight);
      }
    }

    &:not(.--connected):not(.--equalize) {
      ::slotted(sp-info-card) {
        --sp-info-card-group-min-height-sm: v-bind(effectiveMinHeightSm);
      }
    }
  }

  @for $i from 1 through 4 {
    &.--items-per-row-#{$i} {
      grid-template-columns: repeat(#{$i}, 1fr);
    }
  }

  @each $v in default, neutral, inverted {
    &.--variant-#{$v} {
      --gap: var(--#{$v}-group-gap);
      --gap-sm: var(--#{$v}-group-gap-sm);
    }
  }

  &.--connected {
    @include connected-group(
      sp-info-card,
      $bg-var: --background,
      $radius-var: --border-radius,
      $shadow-var: --box-shadow,
      $padding-block-var: --padding-block,
      $padding-inline-var: --padding-inline,
      $divider-color-var: --divider-color,
      $divider-inset-var: --divider-inset
    );

    ::slotted(sp-info-card) {
      // Override SpInfoCard card styling - remove individual card appearance and replace with connected mode styling
      --sp-info-card-group-header-direction: row;
      --sp-info-card-group-header-align: center;
      --sp-info-card-group-text-align: left;

      --sp-info-card-group-padding-block: var(--padding-block-item);
      --sp-info-card-group-padding-inline: var(--padding-inline-item);
      --sp-info-card-group-padding-block-sm: var(--padding-block-item);
      --sp-info-card-group-padding-inline-sm: var(--padding-inline-item);

      --sp-info-card-group-border-radius: 0;
      --box-shadow: none;
    }

    @each $v in default, neutral, inverted {
      &.--variant-#{$v} {
        --border-radius: var(--#{$v}-border-radius);
        --background: var(--#{$v}-background);
        --divider-color: var(--#{$v}-divider-color);
      }
    }

    @each $c in primary, secondary, danger, text, error, warning, success, info, "white", custom {
      &.--variant-inverted.--color-#{$c} {
        --background: var(--sp-sys-color-#{$c});
        --divider-color: var(--sp-ref-palette-#{$c}-darken-1, var(--sp-sys-color-on-#{$c}));
      }
    }
  }
}
</style>
